import { Component, forwardRef, inject, input, linkedSignal, output } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EditorModule } from '@tinymce/tinymce-angular';
import { MaterialModule } from '../../material.module';

@Component({
  imports: [EditorModule, FormsModule, MaterialModule, TranslateModule],
  selector: 'app-editor',
  templateUrl: './editor.component.html',
  styleUrls: ['./editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditorComponent),
      multi: true,
    },
  ],
})
export class EditorComponent implements ControlValueAccessor {
  translate = inject(TranslateService);

  readonly _value = input(undefined);
  readonly _valueViewModel = linkedSignal(() => this._value());
  readonly setup = output<void>();

  editorIsActive = false;
  isDisabled = false;

  public editorConfig: Record<string, any> | undefined = {
    paste_as_text: true,
    height: 500,
    plugins:
      'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table code help wordcount',
    toolbar:
      'undo redo | formatselect | bold italic forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
    sandbox_iframes: false,
    menu: {
      file: { title: 'File', items: '' },
      edit: {
        title: 'Edit',
        items: 'undo redo | cut copy paste | selectall | searchreplace',
      },
      view: {
        title: 'View',
        items: 'code | visualchars visualblocks | spellchecker | preview fullscreen',
      },
      insert: {
        title: 'Insert',
        items:
          'image link media template codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor toc | insertdatetime',
      },
      format: {
        title: 'Format',
        items:
          'bold italic underline strikethrough superscript subscript codeformat | formats blockformats fontformats fontsizes align lineheight | forecolor backcolor | removeformat',
      },
      tools: {
        title: 'Tools',
        items: 'spellchecker spellcheckerlanguage | code wordcount',
      },
      table: {
        title: 'Table',
        items: 'inserttable | cell row column | tableprops deletetable',
      },
      help: { title: 'Help', items: '' },
    },
    file_picker_callback(cb: any): void {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();

        reader.onload = (e) => {
          cb(e.target.result, { title: file.name });
        };
        reader.readAsDataURL(file);
      };
      input.click();
    },

    setup: (editor) => {
      editor.on('SkinLoaded', () => {
        setTimeout(() => {
          this.setup.emit();
        }, 0);
      });
      editor.on('GetContent', (e: any) => {
        e.content = `<div class="tinymce-body">${e.content}</div>`;
      });
    },
  };

  onChange: any = () => {};
  onTouched: any = () => {};

  get value(): any {
    return this._valueViewModel();
  }

  set value(val: any) {
    this._valueViewModel.set(val);
    this.onChange(val);
    this.onTouched();
  }

  updateChanges(): void {
    this.onChange(this.value);
  }

  writeValue(obj: any): void {
    if (obj) {
      this.value = obj;
      this.updateChanges();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  openEditor() {
    this.editorIsActive = true;
  }
}
